import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOURCES = ["Friend", "Social media", "Search", "Other"];
const fieldCls = "mt-1.5 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono placeholder:text-bt-text-tertiary h-11";
const EMPTY = { real_name: "", app_name: "", email: "", source: "", source_detail: "" };

const Field = ({ label, children }) => (
  <div><Label className="text-bt-text-primary font-body text-sm font-medium">{label}</Label>{children}</div>
);

export default function RequestAccessForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: k === "email" ? e.target.value.toLowerCase() : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.source) { setError("Please tell us how you found us"); return; }
    setSending(true); setError("");
    try {
      const { data } = await axios.post(`${BACKEND}/api/access-requests`, form);
      setDone(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send your request. Please try again.");
    }
    setSending(false);
  };

  if (done) return (
    <div className="text-center py-6 space-y-3" data-testid="request-access-success">
      <CheckCircle2 className="w-10 h-10 mx-auto text-bt-positive" />
      <p className="font-heading text-lg text-bt-text-primary">Request received</p>
      <p className="text-sm text-bt-text-secondary font-body">{done}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="request-access-form">
      <p className="text-sm text-bt-text-secondary font-body">Access is by approval. Tell us a little about you and we'll email you once you're in.</p>
      <Field label="Real Name"><Input required value={form.real_name} onChange={set("real_name")} placeholder="Jane Doe" className={fieldCls} data-testid="request-real-name-input" /></Field>
      <Field label="App Name"><Input required maxLength={40} value={form.app_name} onChange={set("app_name")} placeholder="How you'll appear, e.g. CryptoMan" className={fieldCls} data-testid="request-app-name-input" /></Field>
      <Field label="Email Address"><Input required type="email" autoCapitalize="none" value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} data-testid="request-email-input" /></Field>
      <Field label="How did you find us?">
        <Select value={form.source} onValueChange={(v) => { setForm(f => ({ ...f, source: v })); setError(""); }}>
          <SelectTrigger className={fieldCls} data-testid="request-source-select"><SelectValue placeholder="Choose one" /></SelectTrigger>
          <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s} data-testid={`request-source-${s.toLowerCase().replace(" ", "-")}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.source_detail} onChange={set("source_detail")} maxLength={200} placeholder="Anything else? (optional)" className={fieldCls} data-testid="request-source-detail-input" />
      </Field>
      {error && <p className="text-sm text-bt-negative font-body" data-testid="request-access-error">{error}</p>}
      <Button type="submit" disabled={sending} data-testid="request-access-submit"
        className="w-full h-12 bg-bt-surface-2 border border-bt-gold/30 text-bt-gold hover:bg-bt-gold/15 hover:border-bt-gold/50 font-body font-medium text-base transition-colors">
        {sending ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Request Access</>}
      </Button>
    </form>
  );
}
